import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from 'react-icons/fa';

/**
 * کامپوننت انتخاب با قابلیت جستجو
 * @param {Object} props - props کامپوننت
 * @param {Array} props.options - آرایه گزینه‌ها
 * @param {string} props.value - مقدار انتخاب شده
 * @param {Function} props.onChange - تابع تغییر مقدار
 * @param {string} props.placeholder - متن placeholder
 * @param {string} props.searchPlaceholder - متن placeholder جستجو
 * @param {Function} props.getOptionLabel - تابع برای نمایش برچسب گزینه
 * @param {Function} props.getOptionValue - تابع برای دریافت مقدار گزینه
 * @param {boolean} props.isInvalid - آیا فیلد نامعتبر است
 * @param {string} props.errorMessage - پیام خطا
 * @param {string} props.label - برچسب فیلد
 * @param {boolean} props.required - آیا فیلد الزامی است
 * @param {boolean} props.isDisabled - آیا فیلد غیرفعال است
 */
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "انتخاب کنید",
  searchPlaceholder = "جستجو...",
  getOptionLabel = (option) => option.label || option.name || option.title,
  getOptionValue = (option) => option.value || option.id || option._id,
  isInvalid = false,
  errorMessage = "",
  label = "",
  required = false,
  isDisabled = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'white');

  // پیدا کردن گزینه انتخاب شده
  useEffect(() => {
    if (value) {
      const option = options.find(opt => getOptionValue(opt) === value);
      setSelectedOption(option || null);
    } else {
      setSelectedOption(null);
    }
  }, [value, options, getOptionValue]);

  // بستن dropdown با کلیک خارج از آن
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // فیلتر کردن گزینه‌ها بر اساس جستجو
  const filteredOptions = options.filter(option => {
    const label = getOptionLabel(option).toLowerCase();
    const search = searchTerm.toLowerCase();
    return label.includes(search);
  });

  // مدیریت انتخاب گزینه
  const handleOptionSelect = (option) => {
    const optionValue = getOptionValue(option);
    setSelectedOption(option);
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  // پاک کردن انتخاب
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange('');
    setSearchTerm('');
  };

  // مدیریت کلیک روی input
  const handleInputClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  };

  return (
    <Box position="relative" ref={dropdownRef} {...props}>
      {label && (
        <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
          {label} {required && '*'}
        </Text>
      )}
      
      <Box
        position="relative"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        onClick={handleInputClick}
      >
        <Input
          ref={inputRef}
          value={selectedOption ? getOptionLabel(selectedOption) : ''}
          placeholder={placeholder}
          readOnly
          isInvalid={isInvalid}
          isDisabled={isDisabled}
          pr="40px"
          pl="12px"
          bg={bgColor}
          borderColor={isInvalid ? 'red.300' : borderColor}
          _hover={{
            borderColor: isInvalid ? 'red.400' : 'gray.300',
          }}
          _focus={{
            borderColor: isInvalid ? 'red.500' : 'blue.500',
            boxShadow: isInvalid ? '0 0 0 1px #FEB2B2' : '0 0 0 1px #3182CE',
          }}
        />
        
        <HStack position="absolute" right="8px" top="50%" transform="translateY(-50%)" spacing={1}>
          {selectedOption && (
            <IconButton
              size="xs"
              variant="ghost"
              icon={<FaTimes />}
              onClick={handleClear}
              aria-label="پاک کردن"
              color="gray.400"
              _hover={{ color: 'red.400' }}
            />
          )}
          <IconButton
            size="xs"
            variant="ghost"
            icon={isOpen ? <FaChevronUp /> : <FaChevronDown />}
            aria-label="باز کردن منو"
            color="gray.400"
            _hover={{ color: 'gray.600' }}
          />
        </HStack>
      </Box>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left="0"
          right="0"
          zIndex={1000}
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="lg"
          maxH="300px"
          overflow="hidden"
          mt={1}
        >
          {/* جستجو */}
          <Box p={3} borderBottom="1px" borderColor={borderColor}>
            <InputGroup size="sm">
              <InputLeftElement>
                <FaSearch color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                border="none"
                _focus={{ border: 'none', boxShadow: 'none' }}
                bg="transparent"
              />
            </InputGroup>
          </Box>

          {/* لیست گزینه‌ها */}
          <VStack spacing={0} maxH="250px" overflowY="auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = selectedOption && getOptionValue(selectedOption) === optionValue;

                return (
                  <Box
                    key={optionValue || index}
                    w="full"
                    p={3}
                    cursor="pointer"
                    bg={isSelected ? 'blue.50' : 'transparent'}
                    color={isSelected ? 'blue.600' : textColor}
                    _hover={{
                      bg: isSelected ? 'blue.100' : hoverBg,
                    }}
                    onClick={() => handleOptionSelect(option)}
                    borderBottom="1px"
                    borderColor={borderColor}
                    _last={{ borderBottom: 'none' }}
                  >
                    <Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'normal'}>
                      {optionLabel}
                    </Text>
                  </Box>
                );
              })
            ) : (
              <Box p={4} textAlign="center">
                <Text fontSize="sm" color="gray.500">
                  {searchTerm ? 'نتیجه‌ای یافت نشد' : 'گزینه‌ای موجود نیست'}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      )}

      {isInvalid && errorMessage && (
        <Text fontSize="xs" color="red.500" mt={1}>
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};

export default SearchableSelect;
